import { useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import CameraCapture from "@elements/CameraCapture";
import { Feedback } from "@runwayhealth/runway-components-react";
import * as Sentry from "@sentry/react";
import { error_change } from "@store/ui/uiAction";
import { USER_UPDATE } from "@store/user/userActions";
import { setGuardianId } from "@store/user/userSlice";
import { ConditionTypes } from "@types-runway/condition";
import { RootState } from "@types-runway/state";
import { buildFileFormData } from "@utils/forms/formData";

import { user as userApi } from "../../../api";

type GuardianCameraCaptureProps = {
  cam: (value: boolean) => void;
  next: boolean;
  minor: boolean;
  close: (value: boolean) => void;
  userId: string;
};

// Owns the guardian-consent-specific behavior around CameraCapture's generic
// onCapture(file) callback: uploading the file, stamping the tab-local
// guardian ID (setGuardianId), persisting it server-side for logged-in
// guardians (USER_UPDATE), and the post-capture navigation — everything that
// used to live inside WebcamCapture itself (ENG-623).
const GuardianCameraCapture = ({ cam, next, minor, close, userId }: GuardianCameraCaptureProps) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const error = useSelector((state: RootState) => state.ui.error);
  const conditions = useSelector((state: RootState) => state.case.newCase.data.conditions);
  // The case this upload belongs to; stamped onto the ID so a later/other
  // consultation cannot present it as its own (ZD-3226 isolation).
  const activeCaseId = useSelector((state: RootState) => state.case.newCase.data.id);
  // A ref, not state: guards against a second upload firing (e.g. a fast
  // double click on "Use this photo") without needing to reach into
  // CameraCapture to disable anything — it doesn't expose a disabled prop.
  const isUploadingRef = useRef(false);

  const handleCapture = async (file: File) => {
    if (isUploadingRef.current) return;
    isUploadingRef.current = true;

    try {
      const form = buildFileFormData(file, "photo-id");
      const { data, status } = await userApi.userPost("photo-id", form);

      // Always stamp the tab-local guardian ID together with its owning-case
      // marker (guest and logged-in alike) so the value and marker never desync.
      dispatch(
        setGuardianId({
          driver_license_id: data,
          driver_license_case: String(activeCaseId ?? ""),
        })
      );
      // Logged-in guardians also persist the ID to their server profile.
      if (userId !== "") {
        dispatch({ type: USER_UPDATE, userUpdate: { driver_license_id: data } });
      }

      if (status === 200) {
        toast.success("Photo uploaded successfully", { autoClose: 1800 });
        if (minor) {
          cam(false);
          close(false);
        } else if (next) {
          history.push("/id");
        } else {
          const consultations = JSON.parse(sessionStorage.getItem("consultations") ?? "[]");
          if (consultations.includes(ConditionTypes.RUNWAY_PLUS) || conditions.length === 0) {
            history.push("/plus_details");
          }
        }
      }
    } catch (uploadError) {
      // The raw axios error carries this request's FormData (the captured ID
      // photo) on `.config.data` -- never hand it to Sentry as-is, even
      // though today's SDK defaults happen not to serialize that field.
      // Reporting only the message mirrors the same guardrail CameraCapture
      // applies to its own capture-confirm failure path.
      const message = uploadError instanceof Error ? uploadError.message : "Photo ID upload failed";
      Sentry.captureException(new Error(message));
      dispatch(error_change("The photo could not be saved. Please try again."));
      // Deliberately does NOT call cam(false) here — CameraCapture stays on
      // its own captured-photo preview (it never advances state after
      // calling onCapture), so the guardian can retry the same photo via
      // "Use this photo" again, or "Retake" for a new one, without losing
      // their place.
    } finally {
      isUploadingRef.current = false;
    }
  };

  const handleCancel = () => cam(false);

  return (
    <>
      <CameraCapture onCapture={handleCapture} onCancel={handleCancel} />
      {error && (
        <Feedback isInvalid className="rw-modal-error-message">
          {error}
        </Feedback>
      )}
    </>
  );
};

export default GuardianCameraCapture;
